<template>
  <div v-bind:class="contentClass">
    <vue-title :title="$t('identityProvider:securityQuestions:title')"></vue-title>
    <h1 class="section-title">{{ $t('identityProvider:securityQuestions:title') }}</h1>

    <div v-if="model.showError">
      <div class="validation-summary-errors alert alert-danger fade show" role="alert" data-valmsg-summary="true" data-test-element="idp-alert-danger-generic">
        <span class="alert-icon">&#9888;</span>
        <span>{{ model.errorMessage }}</span>
      </div>
      <button type="button" v-on:click="loginCancel" name="Action:RedirectToLogin" class="button" :disabled="disabled">{{ $t('shared:action:back') }}</button>
    </div>

    <div v-else>
      <Form v-slot="{ validate, setErrors, setFieldError }">
        <form ref="form" action="authentication/setupquestions" @submit.prevent="validateBeforeSubmit(validate, setErrors, setFieldError)" autocomplete="off">
          <!-- Summary (client/server) -->
          <div class="validation-summary-errors alert alert-danger fade show"
               role="alert"
               data-valmsg-summary="true"
               data-test-element="security-questions-warning"
               v-if="hasAnyFieldErrors">
            <span class="alert-icon">&#9888;</span>
            <span v-if="formSummaryMessage">{{ formSummaryMessage }}</span>
            <span v-else>{{ $t('validation:summary') }}</span>
          </div>

          <fieldset>
            <div class="panel-message">
              {{ model.instructions }}
            </div>

            <div v-for="(question,i) in model.questions" :key="i">
              <div class="form-group">
                <label class="label" :data-test-element="'security-question-label-' + (i + 1)">{{ $t('identityProvider:securityQuestions:question') }} {{ i+1 }}</label>

                <!-- Multiple availableQuestions -->
                <select v-if="model.availableQuestions.length>1"
                        class="security-question control security-answer-padding"
                        :name="'question'+i"
                        v-model="model.questions[i].id"
                        @change="changeQuestion"
                        :data-test-element="'security-question-' + (i + 1)"
                        :class="{ 'input-validation-error': submitted && !isQuestionValid(i) }">
                  <option value=null>{{ $t('identityProvider:securityQuestions:defaultOption') }}</option>
                  <option v-for="availableQuestion in model.availableQuestions[i]"
                          :key="availableQuestion.id"
                          v-bind:value="availableQuestion.id">
                    {{ availableQuestion.text }}
                  </option>
                </select>

                <!-- Single availableQuestions array -->
                <select v-else-if="model.availableQuestions.length == 1"
                        class="security-question control security-answer-padding"
                        :name="'question'+i"
                        v-model="model.questions[i].id"
                        @change="changeQuestion"
                        :data-test-element="'security-question-' + (i + 1)"
                        :class="{ 'input-validation-error': submitted && !isQuestionValid(i) }">
                  <option value=null>{{ $t('identityProvider:securityQuestions:defaultOption') }}</option>
                  <option v-for="availableQuestion in model.availableQuestions[0]"
                          :key="availableQuestion.id"
                          v-bind:value="availableQuestion.id">
                    {{ availableQuestion.text }}
                  </option>
                </select>

                <!-- Question error -->
                <span class="field-validation-error"
                      :data-test-element="'security-question-error-' + (i + 1)"
                      v-if="submitted && !isQuestionValid(i)">
                  {{ $t('validation:messages:vee-validate:_default').replace('{0}', questionLabel(i)) }}
                </span>
              </div>

              <div class="form-group">
                <label class="label" :data-test-element="'security-answer-label-' + (i + 1)">{{ $t('identityProvider:securityQuestions:answer') }}</label>

                <!-- Answer field; name switches based on isSecureNowEnabled -->
                <Field :name="answerFieldName(i)"
                       :rules="answerRules(answerFieldName(i))"
                       v-model="model.questions[i].answer"
                       v-slot="{ field, meta }">
                  <input type="password"
                         v-bind="field"
                         :name="answerFieldName(i)"
                         class="control"
                         autocomplete="off"
                         :data-test-element="'security-answer-' + (i + 1)"
                         :class="{ 'input-validation-error': submitted && !meta.valid }" />
                </Field>

                <ErrorMessage v-if="submitted" :name="answerFieldName(i)" v-slot="{ message }">
                  <span class="field-validation-error" :data-test-element="'security-answer-error-' + (i + 1)">{{ message }}</span>
                </ErrorMessage>
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" name="Action:SubmitQuestions" class="button" :disabled="disabled" :data-test-element="'security-question-submit'">{{ $t('shared:action:continue') }}</button>
              <a @click.prevent="loginCancel" class="button-secondary link-text-transform" :data-test-element="'security-question-cancel'">{{ $t('shared:action:cancel') }}</a>
            </div>
          </fieldset>
        </form>
      </Form>
    </div>
  </div>
</template>

<script>
  import redirectHandler from 'utils/redirect';
  import ContextualHelp from 'components/popper-help';
  import $ from 'jquery';
  import { Form, Field, ErrorMessage } from 'vee-validate';
  import { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';

  export default {
    name: 'SetupQuestions',
    components: {
      'contextual-help-component': ContextualHelp,
      Form,
      Field,
      ErrorMessage
    },

    data: () => ({
      model: {
        availableQuestions: null,
        instructions: null,
        questions: null
      },
      disabled: false,
      contentClass: 'content-area__content',
      submitted: false,
      hasAnyFieldErrors: false,
      formSummaryMessage: null
    }),

    computed: {
      
    },

    methods: {
      // Answer rules from model.answerComplexity
      answerRules(fieldName) {
        const c = this.model?.answerComplexity || {};
        const rules = { required: true };
        const min = Number(c.answerMinimumLength);
        const max = Number(c.answerMaximumLength);

        if (Number.isFinite(min)) rules.min = min;
        if (Number.isFinite(max)) rules.max = max;
        if (c.answerPattern) {
          try { rules.regex = new RegExp(c.answerPattern); } catch { /* ignore invalid regex */ }
        }
        return rules;
      },

      changeQuestion(event) {
        let question = event.target;
        let selectedValue = question.value;
        var oldValue = $(question).attr('old-value');

        $('select.security-question', this.$refs.form).not(question).each(function () {
          if (typeof oldValue !== typeof undefined && oldValue !== false) {
            $(`option[value="${oldValue}"]`, this).each(function () {
              $(this).removeAttr('disabled');
            });
          }
          $(this).find(`option[value="${selectedValue}"]`).attr('disabled', '');
          $("select option:first-child").removeAttr("disabled");
        });

        if (selectedValue !== '') {
          $(question).attr('old-value', selectedValue);
        } else {
          $(question).removeAttr('old-value');
        }
      },

      questionLabel(i) {
        return `${this.$t('identityProvider:securityQuestions:question')} ${i + 1}`;
      },

      isQuestionValid(i) {
        // Treat null/empty as invalid; mimics required behavior for <select>
        const val = this.model?.questions?.[i]?.id;
        return val !== null && val !== undefined && String(val) !== '' && String(val) !== 'null';
      },

      answerFieldName(i) {
        return this.model?.isSecureNowEnabled ? (`snanswer${i}`) : (`answer${i}`);
      },

      async validateBeforeSubmit(validate, setErrors, setFieldError) {
        this.submitted = true;
        this.formSummaryMessage = null;
        this.hasAnyFieldErrors = false;

        // Client-side validation: ensure all questions selected
        const allQuestionsValid = Array.isArray(this.model?.questions)
          ? this.model.questions.every((q, idx) => this.isQuestionValid(idx))
          : false;

        // Run vee-validate for answers
        const result = await validate();
        const answersValid = result === true || result?.valid === true;

        if (allQuestionsValid && answersValid) {
          await this.post(setErrors, setFieldError);
        } else {
          this.hasAnyFieldErrors = true;
          focusFirstInvalid(this.$refs.form);
        }
      },

      async post(setErrors, setFieldError) {
        const form = this.$refs.form;
        this.disabled = true;
        try {
          let response = await this.$http.post(form.action, this.model);
          redirectHandler.checkAndRedirect(response, this.$router);
        } catch (error) {
          const resp = error?.response;

          // Legacy summary/field mapping
          if (resp && resp.status === 400) {
            // Prefer v4 ModelState mapping; also capture form-level summary
            const consumed = applyServerErrorsFromAxios(error, { setErrors, setFieldError });
            if (consumed) {
              const bag = resp?.data?.errors || resp?.data;
              const summary = bag && (bag[''] || bag['.']);
              const firstSummary = Array.isArray(summary) ? summary.find(Boolean) : (summary || null);
              this.formSummaryMessage = firstSummary || null;
              this.hasAnyFieldErrors = true;
              focusFirstInvalid(form);
              this.disabled = false;
              return;
            }
          }

          try {
            if (this.validateResponse?.(form, resp)) {
              this.hasAnyFieldErrors = true;
              focusFirstInvalid(form);
              return;
            }
          } catch {
            // ignore legacy-path failures
          }

          throw error;
        } finally {
          this.disabled = false;
        }
      },

      async loginCancel() {
        let response = await this.$http.post('authentication/logincancel');
        redirectHandler.checkAndRedirect(response, this.$router);
      }
    },

    async mounted() {
      let form = this.$refs.form;
      let response = await this.$http.get(form.action);
      this.model = response.data;
      this.contentClass = response.data["showError"] ? 'w-100' : this.contentClass;
    }
  };
</script>